export const PICO8_PALETTE = [
  '#000000','#1D2B53','#7E2553','#008751','#AB5236','#5F574F','#C2C3C7','#FFF1E8',
  '#FF004D','#FFA300','#FFEC27','#00E436','#29ADFF','#83769C','#FF77A8','#FFCCAA'
];

const HEX = '0123456789abcdef';

export function hexToRgb(hex: string) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec((hex || '').trim());
  if (!m) return { r: 0, g: 0, b: 0 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

export function isPico8Palette(pal: string[]) {
  if (!pal || pal.length !== 16) return false;
  return pal.every((c, i) => (c || '').toLowerCase() === PICO8_PALETTE[i].toLowerCase());
}

export function nearestPico8Index(colorHex: string) {
  const { r, g, b } = hexToRgb(colorHex || '#000000');
  let best = 0, bestD = Infinity;
  for (let i = 0; i < 16; i++) {
    const p = hexToRgb(PICO8_PALETTE[i]);
    const d = (p.r - r) ** 2 + (p.g - g) ** 2 + (p.b - b) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

export function mapToPicoIndex(v: number, palette: string[]): number {
  if (isPico8Palette(palette) && v >= 0 && v < 16) return v | 0;
  const hex = palette[v] ?? '#000000';
  return nearestPico8Index(hex);
}

// Bygg __gfx__ (128x128 hexrader) med din sprite i (0,0)
export function buildGfx(pixels: number[][], colors: string[]) {
  const H = 128, W = 128;
  const lines: string[] = [];
  const useNative = isPico8Palette(colors);

  for (let y = 0; y < H; y++) {
    if (y < pixels.length) {
      const row = pixels[y];
      let out = '';
      for (let x = 0; x < W; x++) {
        if (x < row.length) {
          const v = row[x] ?? 0;
          let idx = 0;
          if (useNative && v >= 0 && v < 16) idx = v | 0;
          else idx = nearestPico8Index(colors[v] ?? '#000000');
          out += HEX[idx & 15];
        } else out += '0';
      }
      lines.push(out);
    } else {
      lines.push('0'.repeat(W));
    }
  }
  return lines;
}

export function parseGfxLines(originalLines: string[]): number[][] {
  const H = 128, W = 128;
  const grid = Array.from({ length: H }, () => Array(W).fill(0));
  for (let y = 0; y < H; y++) {
    const line = (originalLines[y] || '').trim().toLowerCase();
    for (let x = 0; x < W; x++) {
      const ch = line[x] || '0';
      const v = parseInt(ch, 16);
      grid[y][x] = Number.isNaN(v) ? 0 : v;
    }
  }
  return grid;
}

export function serializeGfxGrid(grid: number[][]): string[] {
  const H = 128, W = 128;
  const out: string[] = [];
  for (let y = 0; y < H; y++) {
    let s = '';
    for (let x = 0; x < W; x++) s += HEX[grid[y][x] & 15];
    out.push(s);
  }
  return out;
}

function findEmptyBlock(grid: number[][], tilesW: number, tilesH: number): { tx: number; ty: number } | null {
  outer: for (let ty = 0; ty <= 16 - tilesH; ty++) {
    for (let tx = 0; tx <= 16 - tilesW; tx++) {
      for (let by = 0; by < tilesH; by++) for (let bx = 0; bx < tilesW; bx++) {
        for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) {
          if (grid[(ty + by) * 8 + y][(tx + bx) * 8 + x] !== 0) continue outer;
        }
      }
      return { tx, ty };
    }
  }
  return null;
}

export function mergeSpriteIntoP8Text(p8Text: string, pixels: number[][], palette: string[]): string {
  const text = p8Text.replace(/\r\n/g, '\n');
  const lines = text.split('\n');
  let gfxStart = lines.findIndex(l => l.trim() === '__gfx__');
  if (gfxStart === -1) { lines.push('__gfx__'); gfxStart = lines.length - 1; }

  let nextSectionRel = lines.slice(gfxStart + 1).findIndex(l => /^__\w+__$/i.test(l.trim()));
  const gfxEnd = nextSectionRel === -1 ? lines.length : gfxStart + 1 + nextSectionRel;

  const existingGfxLines = lines.slice(gfxStart + 1, gfxEnd);
  const grid = parseGfxLines(existingGfxLines);

  const size = pixels?.length === 16 ? 16 : 8;
  const tilesW = size === 16 ? 2 : 1;
  const tilesH = size === 16 ? 2 : 1;
  const pos = findEmptyBlock(grid, tilesW, tilesH);
  if (!pos) throw new Error(`No empty ${size}x${size} sprite slot found in __gfx__.`);

  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const v = pixels?.[y]?.[x] ?? 0;
    grid[pos.ty * 8 + y][pos.tx * 8 + x] = mapToPicoIndex(v, palette);
  }

  const updatedGfx = serializeGfxGrid(grid);
  const pre = lines.slice(0, gfxStart + 1);
  const post = lines.slice(gfxEnd);
  const out = [...pre, ...updatedGfx, ...post].join('\n');
  return out.endsWith('\n') ? out : out + '\n';
}